import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2, AlertTriangle, ShieldAlert, Check, X } from 'lucide-react';
import { aiTextService } from '@/lib/ai-text-service';
import {
  createRewriteRequestGuard,
  runGuardedRewriteRequest,
  type RewriteRequestGuard,
} from '@shared/rewrite-request-guard';
import {
  POSITION_AI_VORLAGEN,
  POSITION_AI_DISCLAIMER,
  resolveAiProposal,
  darfVorschlagUebernommenWerden,
  type PositionAiOutcome,
} from '@shared/position-ai-text';

/**
 * KI-Formulierungshilfe je Position/Titel (Wunsch Malermeister Olli):
 * Vorlagen-Befehle + freies Befehlsfeld auf den Leistungstext EINER Position
 * bzw. EINEN Titel. Bewusst KEIN Stapellauf über alle Positionen — jede
 * Anfrage ist ein einzelner, vom Nutzer ausgelöster Klick.
 *
 * Kein stilles Überschreiben: Der Dialog zeigt Vorher/Nachher; erst
 * "Übernehmen" schreibt (über onApply → Store, nur Textfelder), "Verwerfen"
 * lässt das Original zeichengleich stehen (es wird schlicht nie geschrieben).
 */

export interface PositionAiTextFeld {
  /** 'beschreibung' | 'longText' | 'titel' — der Empfänger (onApply) entscheidet, was damit passiert. */
  key: string;
  label: string;
  text: string;
}

export interface PositionAiTextTarget {
  /** Stabile Kennung für den Request-Guard, z.B. "position:12" oder "titel:Malerarbeiten". */
  scopeKey: string;
  dialogTitle: string;
  gewerk?: string;
  felder: PositionAiTextFeld[];
}

interface PositionAiTextDialogProps {
  target: PositionAiTextTarget | null;
  onClose: () => void;
  /** Wird NUR bei "Übernehmen" aufgerufen — nie automatisch. */
  onApply: (feldKey: string, text: string) => void;
}

interface ProposalState {
  feldKey: string;
  original: string;
  outcome: PositionAiOutcome;
}

export function PositionAiTextDialog({ target, onClose, onApply }: PositionAiTextDialogProps) {
  const [feldKey, setFeldKey] = useState<string>('');
  const [customInstruction, setCustomInstruction] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [proposal, setProposal] = useState<ProposalState | null>(null);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const open = target !== null;
  const felder = target?.felder ?? [];
  const activeFeld = felder.find((f) => f.key === feldKey) ?? felder[0] ?? null;
  const effectiveFeldKey = activeFeld?.key ?? '';
  const originalText = activeFeld?.text ?? '';

  const guardRef = useRef<RewriteRequestGuard | null>(null);
  const scope = `${target?.scopeKey ?? ''}:${effectiveFeldKey}`;
  const guard = guardRef.current ?? createRewriteRequestGuard(originalText, scope);
  guardRef.current = guard;
  guard.updateScope(scope);
  guard.updateSource(originalText);

  // Dialog-Wechsel (anderes Ziel / geschlossen): Zustand zurücksetzen.
  useEffect(() => {
    setFeldKey(target?.felder[0]?.key ?? '');
    setCustomInstruction('');
    setProposal(null);
    setServiceError(null);
    setIsBusy(false);
    return () => {
      guard.invalidate();
    };
  }, [target?.scopeKey, guard]);

  const switchFeld = (key: string) => {
    if (key === effectiveFeldKey) return;
    guard.invalidate();
    setFeldKey(key);
    setProposal(null);
    setServiceError(null);
    setIsBusy(false);
  };

  const runInstruction = async (instruction: string) => {
    const trimmedInstruction = instruction.trim();
    if (!trimmedInstruction || !activeFeld) return;
    if (!originalText.trim()) {
      setServiceError('Dieses Feld ist leer — bitte zuerst einen Text eingeben.');
      return;
    }

    setIsBusy(true);
    setServiceError(null);
    setProposal(null);

    try {
      await runGuardedRewriteRequest(
        guard,
        (signal) => aiTextService.rewriteText(originalText, trimmedInstruction, target?.gewerk, signal),
        (result) => {
          if (!result.success) {
            setServiceError(result.error || 'Unbekannter Fehler bei der KI-Textüberarbeitung.');
            return;
          }
          setProposal({
            feldKey: activeFeld.key,
            original: originalText,
            outcome: resolveAiProposal(originalText, result),
          });
        },
        { onLatestSettled: () => setIsBusy(false) },
      );
    } catch (error) {
      console.error('Position AI rewrite error:', error);
      setServiceError('Ein unerwarteter Fehler ist aufgetreten. Der Text bleibt unverändert.');
      setIsBusy(false);
    }
  };

  const handleVerwerfen = () => {
    // Es wurde nie geschrieben — Original bleibt zeichengleich erhalten.
    setProposal(null);
  };

  const handleUebernehmen = () => {
    if (!proposal || proposal.outcome.kind !== 'vorschlag') return;
    if (!darfVorschlagUebernommenWerden(proposal.outcome)) return;
    onApply(proposal.feldKey, proposal.outcome.vorschlag);
    onClose();
  };

  const outcome = proposal?.outcome ?? null;
  const review = outcome?.kind === 'vorschlag' ? outcome.review : null;
  const uebernahmeErlaubt = outcome !== null && darfVorschlagUebernommenWerden(outcome);
  const istUnkonfiguriert = serviceError !== null && /nicht konfiguriert/i.test(serviceError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" />
            {target?.dialogTitle ?? 'KI-Formulierungshilfe'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{POSITION_AI_DISCLAIMER}</p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
          {/* Feld-Auswahl: Kurz-/Langtext sind zwei verschiedene Dinge — getrennt und sichtbar. */}
          {felder.length > 1 && (
            <div className="flex gap-1.5">
              {felder.map((feld) => (
                <Button
                  key={feld.key}
                  type="button"
                  size="sm"
                  variant={feld.key === effectiveFeldKey ? 'secondary' : 'outline'}
                  onClick={() => switchFeld(feld.key)}
                  disabled={isBusy}
                  className="text-xs"
                >
                  {feld.label}
                </Button>
              ))}
            </div>
          )}

          {/* Vorlagen + freier Befehl */}
          <div className="flex flex-wrap gap-1.5">
            {POSITION_AI_VORLAGEN.map((vorlage) => (
              <Button
                key={vorlage.id}
                type="button"
                size="sm"
                variant="secondary"
                disabled={isBusy}
                onClick={() => runInstruction(vorlage.instruction)}
                className="h-auto py-1 px-2 text-xs whitespace-normal text-left"
                title={vorlage.instruction}
              >
                {vorlage.label}
              </Button>
            ))}
          </div>

          <div className="flex gap-1.5 items-start">
            <Textarea
              value={customInstruction}
              onChange={(e) => setCustomInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  runInstruction(customInstruction);
                }
              }}
              placeholder="Eigener Befehl, z.B. mach das etwas professioneller… (Enter zum Ausführen)"
              rows={2}
              disabled={isBusy}
              className="text-sm flex-1"
            />
            <Button
              type="button"
              size="sm"
              disabled={isBusy || customInstruction.trim().length === 0}
              onClick={() => runInstruction(customInstruction)}
              className="flex-shrink-0"
            >
              {isBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {isBusy && (
            <p className="text-xs text-purple-600 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              KI formuliert&hellip; Der Text wird erst nach Ihrer Freigabe übernommen.
            </p>
          )}

          {/* Nicht konfiguriert: verständliche Ansage statt kaputter Knopf. */}
          {serviceError && (
            <div
              role="alert"
              className={`rounded-md border p-2 text-xs ${istUnkonfiguriert
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-red-200 bg-red-50 text-red-700'}`}
            >
              {istUnkonfiguriert
                ? 'Die KI-Funktion ist noch nicht eingerichtet (kein OpenAI-Schlüssel hinterlegt). Ihre Texte bleiben unverändert — bitte den Administrator ansprechen.'
                : serviceError}
            </div>
          )}

          {/* Leere/unvollständige Antwort: nichts geändert. */}
          {outcome?.kind === 'unveraendert' && (
            <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              {outcome.grund} Das Original bleibt unverändert.
            </div>
          )}

          {/* Vorher/Nachher — der Nutzer entscheidet. */}
          {outcome?.kind === 'vorschlag' && proposal && (
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Vorher (aktueller Text)</p>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {proposal.original}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-purple-700 mb-1">Nachher (KI-Vorschlag)</p>
                  <div className="rounded-md border border-purple-200 bg-purple-50 p-2 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {outcome.vorschlag}
                  </div>
                </div>
              </div>

              {/* Der Server repariert zeilenweise: fehlt eine Pflichtzeile woertlich,
                  haengt er sie an. Bei einzeiligen Beschreibungen ist diese "Zeile"
                  der ganze Satz — dann steht die alte Fassung als Dopplung unter dem
                  Vorschlag. Ehrlich benennen statt den Nutzer raten lassen. */}
              {outcome.safeguarded && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
                  {outcome.note || 'Pflichtangaben wurden automatisch geschützt/wiederhergestellt.'}
                  {' '}
                  Die geschützte Fassung wurde dafür unten angehängt &mdash; bitte prüfen und
                  eine eventuelle Dopplung nach dem Übernehmen von Hand entfernen.
                </div>
              )}

              {review?.verdict === 'abgelehnt' && (
                <div role="alert" className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 space-y-1">
                  <p className="font-medium flex items-center gap-1.5">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Vorschlag gesperrt: geschützte Pflichtangaben fehlen im KI-Text.
                  </p>
                  <ul className="list-disc pl-4">
                    {review.fehlendeKritischeAngaben.map((angabe) => (
                      <li key={angabe}>{angabe}</li>
                    ))}
                  </ul>
                  <p>Der Originaltext bleibt unverändert. Bitte verwerfen und ggf. mit anderem Befehl erneut versuchen.</p>
                </div>
              )}

              {review?.verdict === 'warnung' && (
                <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 space-y-1">
                  <p className="font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Bitte prüfen: Diese Angaben aus dem Original finden sich nicht wörtlich im Vorschlag.
                  </p>
                  <ul className="list-disc pl-4">
                    {[...review.fehlendeTechnischeAngaben, ...review.fehlendeZahlAngaben].map((angabe) => (
                      <li key={angabe}>{angabe}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleVerwerfen}>
                  <X className="h-3.5 w-3.5 mr-1.5" />
                  Verwerfen
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleUebernehmen}
                  disabled={!uebernahmeErlaubt}
                  className="bg-purple-600 hover:bg-purple-700"
                  title={uebernahmeErlaubt ? undefined : 'Übernahme gesperrt, weil geschützte Pflichtangaben fehlen'}
                >
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  Übernehmen
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PositionAiTextDialog;
